import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import qs from 'qs';
import { observer } from 'mobx-react';
import {
  Box,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Icon,
  Button,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import CustomTable from 'components/common/customTableV2';
import PageHeader from 'components/common/pageHeader';
import pageHeaderStyles from 'components/common/pageHeader/styles';
import WinesService from 'services/winesService';
import wineListImportService from 'services/wineListImportService';
import { adaptWineLibraryBrowseTableData } from 'helpers/adapter';
import { replacePath, getMessage } from 'helpers/helper';
import domainData from 'store/domainStore';
import SidebarStore from 'store/ui/sidebarStore';
import M from 'messages';
import { routes } from 'config/index';
import { validateFilterParams } from 'store/common/filterUtils';
import { useModal } from 'hooks/common';

import tableOptions from './config';
import wineLibraryStyles from './styles';
import CustomTableToolbar from '../toolbar';
import WineListImportModal from '../../welcome/modals/wineListImportModal';
import classes from '../styles';

const defaultFilterParams = {
  params: {
    sort: {
      field: 'name',
      order: 'asc',
    },
    filter: {},
    limit: 50,
    skip: 0,
    search: {
      value: '',
      fields: [],
    },
  },
};

function getWineLibraryBrowseInitialParams(tableConfig, userContext, urlParams) {
  if (tableConfig && urlParams && Object.keys(urlParams).length > 0) {
    const validated = validateFilterParams(urlParams, tableConfig, userContext);
    if (validated) {
      return { params: validated };
    }
  }
  return JSON.parse(JSON.stringify(defaultFilterParams));
}

const WineLibraryBrowse = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = domainData.getCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const pageParams = qs.parse(location.search, { ignoreQueryPrefix: true });
  const isSidebarOpened = SidebarStore.sidebarState;

  const [dataLoading, setDataLoading] = useState({ loading: true, loaded: false });
  const [winesData, setWinesData] = useState({ count: 0, data: [] });
  const [selectedRows, setSelectedRows] = useState([]);
  const [aiEnhancementInfoOpen, setAiEnhancementInfoOpen] = useState(false);
  const [isWineListImportOpen, openWineListImportModal, closeWineListImportModal] = useModal(false);

  const userContext = { isUserSuperAdmin: false, subscriptionAccess: {} };

  const [queryParams, setQueryParamsState] = useState(() =>
    getWineLibraryBrowseInitialParams(tableOptions.wineLibraryBrowse, userContext, pageParams),
  );

  const setFilter = useCallback(
    filter => {
      replacePath(navigate, filter);
      setQueryParamsState(filter);
    },
    [navigate],
  );

  const { searchFields, filterFields } = tableOptions.wineLibraryBrowse;

  const getWinesLists = useCallback(
    async params => {
      try {
        setDataLoading({ loaded: false });
        setFilter(params);
        const listParams = { ...params.params };
        delete listParams.fromWineLibraries;
        const newParams = {
          params: {
            ...listParams,
            fromWineLibraries: true,
            includeLists: false,
            includeDrafts: true,
          },
        };
        const response = await WinesService.getWines(newParams);
        if (response) {
          setWinesData({
            count: response.data.count,
            data: adaptWineLibraryBrowseTableData(response.data.data),
            createAbility: response.data.createAbility,
          });
        }
      } catch (err) {
        enqueueSnackbar('Get wines lists error', { variant: 'error' });
      } finally {
        setDataLoading({ loading: false, loaded: true });
      }
    },
    [enqueueSnackbar, setFilter],
  );

  useEffect(() => {
    const pp = qs.parse(location.search, { ignoreQueryPrefix: true });
    const qp = getWineLibraryBrowseInitialParams(tableOptions.wineLibraryBrowse, userContext, pp);
    setQueryParamsState(qp);
    getWinesLists(qp);
    setSelectedRows([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleWineListImportSubmit = async payload => {
    await wineListImportService.requestWineListImport(payload);
  };

  const handleCreateNewWine = () => {
    navigate(routes.wineCreate.path);
  };

  const onSearchCallback = value => {
    let oldValue = queryParams.params?.search?.value;
    if (oldValue === undefined) {
      oldValue = '';
    }
    if (searchFields && oldValue !== value) {
      const newFilter = {
        params: {
          ...queryParams.params,
          search: { ...queryParams.params.search, value, fields: searchFields },
          skip: 0,
        },
      };
      if (!value) {
        delete newFilter.params.search;
      }
      setFilter(newFilter);
      getWinesLists(newFilter);
    }
  };

  const onFilterCallback = (filterObj, reset = false) => {
    if (reset) {
      const clean = { ...defaultFilterParams };
      getWinesLists(clean);
      return;
    }
    if (filterObj && !Array.isArray(filterObj)) {
      const fieldKeyList = Object.keys(filterObj);
      const baseFilter = { ...(queryParams.params?.filter || {}) };
      let nextFilter = { ...baseFilter };
      let changed = false;
      fieldKeyList.forEach(i => {
        const fieldValue = filterObj[i];
        const oldValue = queryParams.params?.filter?.[i] ?? '';
        if (fieldValue === '' || fieldValue === null) {
          if (Object.prototype.hasOwnProperty.call(nextFilter, i)) {
            delete nextFilter[i];
            changed = true;
          }
        } else if (filterFields && fieldValue !== undefined && oldValue !== fieldValue) {
          nextFilter[i] = fieldValue;
          changed = true;
        }
      });
      if (changed) {
        const newFilter = {
          params: {
            ...queryParams.params,
            filter: nextFilter,
            skip: 0,
          },
        };
        setFilter(newFilter);
        getWinesLists(newFilter);
      }
    }
  };

  const handleCopyFromLibrary = async () => {
    if (!currentUser?.serviceProviderId || !selectedRows.length) return;
    const addedCount = selectedRows.length;
    try {
      await WinesService.copyToWines({
        serviceProviderId: currentUser.serviceProviderId,
        ids: selectedRows,
        action: 'copy',
      });
      enqueueSnackbar(M.get('wines.copyFromLibrarySuccess').replace('{count}', String(addedCount)), {
        variant: 'success',
      });
      setSelectedRows([]);
    } catch (error) {
      enqueueSnackbar(getMessage(error?.response?.data, 'error'), { variant: 'error' });
    }
  };

  const toolbarView = (
    <CustomTableToolbar
      onSearchCallback={onSearchCallback}
      filteredParams={queryParams}
      numSelected={selectedRows.length}
      filterFields={filterFields}
      onFilterCallback={onFilterCallback}
      handleDelete={undefined}
      handleCopy={handleCopyFromLibrary}
      isUserSuperAdmin={false}
      isLibraryBrowseMode={true}
    />
  );

  const hasLibrarySearchOrFilter = !!(
    (queryParams?.params?.filter && Object.keys(queryParams.params.filter).length > 0) ||
    (queryParams?.params?.search?.value && String(queryParams.params.search.value).trim().length > 0)
  );

  const wineLibraryEmptyRowActions =
    hasLibrarySearchOrFilter && dataLoading.loaded && Array.isArray(winesData?.data) && winesData.data.length === 0 ? (
      <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Button
          variant="outlined"
          color="primary"
          sx={classes.primaryIconButtonTable}
          onClick={openWineListImportModal}
          startIcon={<Icon baseClassName="material-symbols-outlined">upload_file</Icon>}>
          {M.get('actions.importWineList')}
        </Button>
        {winesData?.createAbility === false ? (
          <Tooltip title={M.get('wines.createLimit')} placement="top">
            <Box component="span" sx={{ display: 'inline-block' }}>
              <Button
                variant="outlined"
                color="primary"
                sx={classes.primaryIconButtonTable}
                disabled
                startIcon={<Icon baseClassName="material-symbols-outlined">edit</Icon>}>
                {M.get('actions.createNewWine')}
              </Button>
            </Box>
          </Tooltip>
        ) : (
          <Button
            variant="outlined"
            color="primary"
            sx={classes.primaryIconButtonTable}
            onClick={handleCreateNewWine}
            startIcon={<Icon baseClassName="material-symbols-outlined">edit</Icon>}>
            {M.get('actions.createNewWine')}
          </Button>
        )}
      </Box>
    ) : null;

  return (
    <Box
      sx={{
        ...classes.root,
        ...(isSidebarOpened && classes.rootDrawerOpen),
        ...(!isSidebarOpened && classes.rootDrawerClose),
      }}>
      <PageHeader generalPageTitle={M.get('wines.libraryBrowseTitle')} onlyGeneralPageTitle />
      <Box sx={wineLibraryStyles.headerSection}>
        <Box sx={pageHeaderStyles.aboveTitle}>
          <Link component={RouterLink} to={routes.wines.path} sx={wineLibraryStyles.returnToWinesLink}>
            <ArrowBackIcon sx={wineLibraryStyles.returnToWinesIcon} aria-hidden />
            {M.get('wines.libraryBrowseReturnAboveTitle')}
          </Link>
        </Box>
        <Box sx={pageHeaderStyles.titleContainer}>
          <Typography sx={pageHeaderStyles.title}>{M.get('wines.libraryBrowseTitle')}</Typography>
        </Box>
        <Typography component="div" sx={pageHeaderStyles.description}>
          {M.get('wines.libraryBrowseDescriptionPrefix')}
          <Link
            component="button"
            type="button"
            onClick={() => setAiEnhancementInfoOpen(true)}
            sx={wineLibraryStyles.aiEnhancementTrigger}>
            <Icon baseClassName="material-symbols-outlined" sx={wineLibraryStyles.aiEnhancementTriggerIcon} aria-hidden>
              stars_2
            </Icon>
            {M.get('wines.libraryBrowseAiEnhancementLink')}
          </Link>
          {M.get('wines.libraryBrowseDescriptionSuffix')}
        </Typography>
      </Box>
      <WineListImportModal
        open={isWineListImportOpen}
        handleClose={closeWineListImportModal}
        handleSubmit={handleWineListImportSubmit}
        serviceProviderId={currentUser?.serviceProviderId}
      />
      <Dialog
        open={aiEnhancementInfoOpen}
        onClose={() => setAiEnhancementInfoOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="wine-library-ai-enhancement-title">
        <DialogTitle id="wine-library-ai-enhancement-title" sx={wineLibraryStyles.aiDialogTitle}>
          <Box sx={wineLibraryStyles.aiDialogTitleGroup}>
            <Icon baseClassName="material-symbols-outlined" sx={wineLibraryStyles.aiDialogTitleIcon} aria-hidden>
              stars_2
            </Icon>
            <Typography component="span" variant="h6" sx={wineLibraryStyles.aiDialogTitleText}>
              {M.get('wines.aiEnhancementInfoModal.title')}
            </Typography>
          </Box>
          <IconButton aria-label="Close" edge="end" onClick={() => setAiEnhancementInfoOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography paragraph variant="body2" component="div">
            {M.get('wines.aiEnhancementInfoModal.introPrefix')}
            <Link
              href={`mailto:${M.get('wines.aiEnhancementInfoModal.supportEmail')}`}
              sx={wineLibraryStyles.modalMailLink}>
              {M.get('wines.aiEnhancementInfoModal.supportEmail')}
            </Link>
            {M.get('wines.aiEnhancementInfoModal.introSuffix')}
          </Typography>
          <Typography variant="subtitle2" sx={wineLibraryStyles.modalTierHeadingFirst}>
            {M.get('wines.aiEnhancementInfoModal.bottleImageTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={wineLibraryStyles.modalTierBodyFirst}>
            {M.get('wines.aiEnhancementInfoModal.bottleImageDescription')}
          </Typography>
          <Typography variant="subtitle2" sx={wineLibraryStyles.modalTierHeading}>
            {M.get('wines.aiEnhancementInfoModal.fullProfileTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {M.get('wines.aiEnhancementInfoModal.fullProfileDescription')}
          </Typography>
          <Typography variant="body2" sx={wineLibraryStyles.modalConfirmation}>
            {M.get('wines.aiEnhancementInfoModal.confirmationText')}
          </Typography>
        </DialogContent>
      </Dialog>
      <Grid size={{ xs: 12 }}>
        <Box sx={classes.tableRoot}>
          <Grid container sx={classes.container}>
            <Box sx={classes.tabsContainer}>
              <CustomTable
                tableSources={winesData}
                tableOptions={tableOptions.wineLibraryBrowse}
                loading={dataLoading.loading}
                filteredParams={queryParams}
                setFilteredParams={setFilter}
                getDataOnChange={getWinesLists}
                toolbarView={toolbarView}
                setSelectedRows={setSelectedRows}
                selectedRows={selectedRows}
                withOpenAction={false}
                withEditAction={false}
                disableCondition={row => !row.active}
                withCheckbox
                emptyRowSlot={wineLibraryEmptyRowActions}
              />
            </Box>
          </Grid>
        </Box>
      </Grid>
    </Box>
  );
});

export default WineLibraryBrowse;
